import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { SecureChatProvider, useSecureChat } from "./SecureChatContext";
import ChatList from "./ChatList";
import MessageArea from "./MessageArea";
import { NewAuthNav } from "../../Layout/NewAuthNav";
import { useLocation } from "react-router-dom";
const api = require("../../api/secureMessageApi");

// Main layout component
const SecureChatLayoutComponent = () => {
  const { currentUser, error, selectChat } = useSecureChat();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showMessageArea, setShowMessageArea] = useState(false);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setShowMessageArea(false); // Reset on desktop
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Show message area when chat is selected on mobile
  const handleChatSelect = (chatId) => {
    if (isMobile) {
      setShowMessageArea(true);
    }
  };

  // Close message area on mobile
  const handleCloseMessageArea = () => {
    setShowMessageArea(false);
  };

  // When opened with ?otherUserId=..., create or reuse 1:1 secure chat and select it
  useEffect(() => {
    if (!currentUser) return;

    const params = new URLSearchParams(location.search);
    const otherUserId = params.get("otherUserId");
    if (!otherUserId) return;

    let cancelled = false;

    (async () => {
      try {
        const response = await api.createChat(otherUserId);
        const chatData = response?.data || response;

        if (!chatData || !chatData._id || cancelled) return;

        // Select this chat so MessageArea loads it
        selectChat(chatData._id);

        // On mobile, ensure the message area is visible
        if (window.innerWidth <= 768) {
          setShowMessageArea(true);
        }
      } catch (e) {
        console.error(
          "Error creating/ensuring 1:1 secure chat for otherUserId from URL:",
          e,
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUser, location.search, selectChat]);

  if (error) {
    return (
      <PageContainer>
        <NewAuthNav />
        <ErrorContainer>
          <ErrorText>Error: {error}</ErrorText>
          <RetryButton onClick={() => window.location.reload()}>
            Retry
          </RetryButton>
        </ErrorContainer>
      </PageContainer>
    );
  }

  if (!currentUser) {
    return (
      <PageContainer>
        <NewAuthNav />
        <LoadingContainer>
          <LoadingText>Loading...</LoadingText>
        </LoadingContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <NewAuthNav />
      <ContentWrapper>
        <LayoutContainer>
          <ChatListContainer
            $isMobile={isMobile}
            $showMessageArea={showMessageArea}
          >
            <ChatList onChatSelect={handleChatSelect} />
          </ChatListContainer>

          <MessageAreaContainer
            $isMobile={isMobile}
            $showMessageArea={showMessageArea}
          >
            <MessageArea onClose={handleCloseMessageArea} />
          </MessageAreaContainer>
        </LayoutContainer>
      </ContentWrapper>
    </PageContainer>
  );
};

// Main component with provider
const SecureChatLayout = () => {
  return (
    <SecureChatProvider>
      <SecureChatLayoutComponent />
    </SecureChatProvider>
  );
};

export default SecureChatLayout;

// Styled Components
const PageContainer = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  /* Ensure the global Layout background is visible if this component is rendered inside it.
     However, if this component REPLACES Layout, it needs to provide the background itself.
     Based on typical routing, full page components might need to replicate the global background style
     OR be wrapped in the Layout component.
     Given the user request "integrated the theming similar to @Create.js", and Create.js uses <Layout>,
     we should ideally wrap this in <Layout> or replicate the background style.
     
     The user query says: "It's probably incorporated in @Layout.js , check how it is integrated in @Create.js for reference."
     Create.js wraps its content in <Layout>.
     SecureChatLayout seems to be a standalone page layout that includes NewAuthNav directly.
     If we can't wrap it in Layout easily (because of structure), we should replicate the background style here.
  */
  background-color: var(--color-background);
  background-image: linear-gradient(rgba(0, 0, 0, 0.3) 1px, transparent 1px);
  background-size: 100% 25px;
  overflow: hidden;
`;

const ContentWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  max-width: 1920px;
  margin-top: 0;
  background-color: transparent;
  overflow: hidden;
`;

const LayoutContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  background-color: white;
`;

const ChatListContainer = styled.div`
  width: ${(props) => (props.$isMobile ? "100%" : "30%")};
  min-width: ${(props) => (props.$isMobile ? "100%" : "300px")};
  max-width: ${(props) => (props.$isMobile ? "100%" : "400px")};
  display: ${(props) =>
    props.$isMobile && props.$showMessageArea ? "none" : "flex"};
  flex-direction: column;
  background-color: var(--color-surface);
  border-right: ${(props) =>
    props.$isMobile ? "none" : "1px solid var(--color-border)"};
  box-shadow: ${(props) =>
    props.$isMobile ? "none" : "2px 0 4px rgba(0,0,0,0.05)"};
  z-index: 2;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    position: ${(props) => (props.$showMessageArea ? "absolute" : "relative")};
    left: ${(props) => (props.$showMessageArea ? "-100%" : "0")};
    width: 100%;
    height: 100%;
  }
`;

const MessageAreaContainer = styled.div`
  width: ${(props) => (props.$isMobile ? "100%" : "70%")};
  display: ${(props) =>
    props.$isMobile ? (props.$showMessageArea ? "flex" : "none") : "flex"};
  flex-direction: column;
  background-color: transparent;
  position: ${(props) => (props.$isMobile ? "absolute" : "relative")};
  top: ${(props) => (props.$isMobile ? "0" : "auto")};
  left: ${(props) => (props.$isMobile ? "0" : "auto")};
  height: 100%;
  z-index: 3;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    width: 100%;
    background-color: var(--color-surface);
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  background-color: transparent;
`;

const ErrorText = styled.p`
  color: var(--color-text);
  font-size: 16px;
  margin-bottom: 16px;
  text-align: center;
`;

const RetryButton = styled.button`
  background-color: transparent;
  color: var(--color-text);
  border: 1px solid var(--color-border);
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background-color: var(--color-surface);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  background-color: transparent;
`;

const LoadingText = styled.p`
  color: var(--color-text);
  font-size: 16px;
`;
